/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { Route, RouteComponentProps } from 'react-router';
import { oc } from 'ts-optchain';
import _ from 'lodash';

import { DefaultConnectProps } from '@tock/shared/utils/connect';
import { IWithContextProps } from '@tock/coretypes';
import { logger } from '@tock/logging';
import { parse } from '@tock/query-string';
import { urlWithQuery } from 'utils/domainUtils';
import BaseComponent from '@tock/shared/components/BaseComponent';

// Type FlowContext in this file refers to data (often the props of the component rendering the FlowRoute)
// that is intended to be utilizable by the FlowRoute.
// e.g. in CheckoutRoute, which renders this, FlowContext is equal to <CheckoutRoute>'s props.
// e.g. in ShopCheckoutRoute, this is a context object made with data fetched through hooks

export interface Page<FlowContext> {
  path: string;
  invalidIf(props: FlowContext): boolean;
  shouldShow(props: FlowContext): boolean;
}

export interface FlowRouteComponentProps {
  currentRoute: string;
  nextRoute: string;
  prevRoute: string;
}

export interface State<FlowContext> {
  currentPage?: Page<FlowContext>;
  nextPage?: Page<FlowContext>;
  prevPage?: Page<FlowContext>;
}

interface PassedDownProps extends RouteComponentProps {
  pageOrder: Array<Page<any>>;
  isInitialized: boolean;
  isInQuickCheckout?: boolean;
  path: string;
  component?: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
  render?(routeProps: RouteComponentProps, flowProps: FlowRouteComponentProps): React.ReactNode;
}

export type FlowRouteProps<FlowContext> = DefaultConnectProps & PassedDownProps & FlowContext;

class FlowRoute<FlowContext> extends BaseComponent<
  FlowRouteProps<FlowContext> & IWithContextProps,
  State<FlowContext>
> {
  constructor(props: FlowRouteProps<FlowContext> & IWithContextProps) {
    super(props);
    const currentPage = this.getCurrentPage(props);
    this.state = {
      currentPage,
    };
  }

  UNSAFE_componentWillMount() {
    if (BROWSER) {
      const currentPage = this.getCurrentPage(this.props);
      const isCurrentPageValid = this.isCurrentPageValid(currentPage, this.props);

      if (!isCurrentPageValid) {
        return;
      }

      const pageState = this.computePageState(this.props);
      this.setState(pageState);

      if (!currentPage.shouldShow(this.props)) {
        const query = parse(this.props.location.search);
        // Including search parameters in the redirect to maintain tock_source context (specifically regarding whether a user originated from the widget)
        this.props.history.replace(urlWithQuery(pageState.nextPage.path, query));
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: FlowRouteProps<FlowContext> & IWithContextProps) {
    const currentPage = this.getCurrentPage(nextProps);
    const isCurrentPageValid = this.isCurrentPageValid(currentPage, nextProps);

    if (!isCurrentPageValid) {
      return;
    }

    const pageState = this.computePageState(nextProps);
    this.setState(pageState);

    if (
      !pageState.currentPage.shouldShow(nextProps) &&
      pageState.nextPage !== pageState.currentPage
    ) {
      this.routeTo(pageState.nextPage);
    }
  }

  routeTo(nextPage: Page<FlowContext>) {
    const { actions, history } = this.props;
    actions.checkout.setCheckoutStatus('', '');
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    history.replace(oc(nextPage).path()!);
  }

  isCurrentPageValid(
    currentPage: Page<FlowContext>,
    props: FlowRouteProps<FlowContext> & IWithContextProps
  ): boolean {
    if (!currentPage) {
      logger.debug('Could not find a page for path', this.props.location.pathname);
      return false;
    }

    if (currentPage.invalidIf(props)) {
      props.routeTo('/');
      return false;
    }

    return true;
  }

  getCurrentPage(props: FlowRouteProps<FlowContext> & IWithContextProps): Page<FlowContext> {
    const path = props.location.pathname;

    const currentPage = _.find(this.props.pageOrder, (page) => {
      return _.endsWith(path, page.path);
    });
    return currentPage as Page<FlowContext>;
  }

  computePageState(props: FlowRouteProps<FlowContext> & IWithContextProps) {
    const currentPage = this.getCurrentPage(props);
    const nextPage = this.findNextPage(currentPage, props);
    const prevPage = this.findPrevPage(currentPage, props);
    return {
      currentPage,
      nextPage,
      prevPage,
    };
  }

  findNextPage(
    currentPage: Page<FlowContext>,
    props: FlowRouteProps<FlowContext> & IWithContextProps
  ): Page<FlowContext> {
    const { pageOrder } = this.props;
    const currentIndex = _.indexOf(pageOrder, currentPage);
    let nextIndex = currentIndex + 1;
    do {
      if (nextIndex >= pageOrder.length) {
        return pageOrder[pageOrder.length - 1];
      }
      if (!pageOrder[nextIndex].shouldShow(props)) {
        nextIndex++;
        continue;
      }
      return pageOrder[nextIndex];
    } while (nextIndex < pageOrder.length);
    // Could not find a valid page to transition to
    return pageOrder[currentIndex];
  }

  findPrevPage(
    currentPage: Page<FlowContext>,
    props: FlowRouteProps<FlowContext> & IWithContextProps
  ): Page<FlowContext> {
    const { pageOrder } = this.props;
    const currentIndex = _.indexOf(pageOrder, currentPage);
    let prevIndex = currentIndex - 1;
    do {
      if (prevIndex < 0) {
        return pageOrder[0];
      }
      if (!pageOrder[prevIndex].shouldShow(props)) {
        prevIndex--;
        continue;
      }
      return pageOrder[prevIndex];
    } while (prevIndex >= 0);
    // Could not find a valid page to transition to
    return pageOrder[currentIndex];
  }

  computeFlowRouteProps(state: State<FlowContext>): FlowRouteComponentProps {
    return {
      currentRoute: oc(state.currentPage).path(''),
      nextRoute: oc(state.nextPage).path(''),
      prevRoute: oc(state.prevPage).path(''),
    };
  }

  renderRoute(routeProps: RouteComponentProps) {
    if (this.props.component) {
      return React.createElement(this.props.component as any, {
        ...routeProps,
        ...this.computeFlowRouteProps(this.state),
      });
    } else if (this.props.render) {
      return this.props.render(routeProps, this.computeFlowRouteProps(this.state));
    } else {
      return null;
    }
  }

  render() {
    if (!this.props.isInitialized) {
      return null;
    }
    // The way that props is defined here is so complex that Typescript doesn't seem to
    // be sure this.props.path is a string! (It is.) We extract it with an explicit typed
    // assignment rather than destructuring to make this clear, both to the reader and to the
    // instantiation of the <Route> component below.
    const path: string = this.props.path;

    return <Route path={path} render={this.renderRoute} />;
  }
}

export default FlowRoute;
